import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, 
  List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, 
  Typography, Box, Autocomplete, CircularProgress 
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import api from '../../api';

const ChannelSettingsModal = ({ open, onClose, channel, currentEmpId, onChannelDeleted, onChannelUpdated }) => {
  const [members, setMembers] = useState([]);
  const [imageUrl, setImageUrl] = useState('');
  
  // New States for Autocomplete Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (open && channel) {
      setImageUrl(channel.image_url || '');
      setSelectedEmployee(null);
      setSearchQuery('');
      setSearchResults([]);
      fetchMembers();
    }
  }, [open, channel]);

  const fetchMembers = () => {
    api.get(`/chat/channels/${channel.id}/members`)
       .then(res => setMembers(res.data))
       .catch(err => console.error(err));
  };

  // Debounced search effect
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const delayDebounceFn = setTimeout(() => {
      api.get(`/chat/search-employees?q=${searchQuery}&current_emp_id=${currentEmpId}`)
        .then(res => {
          // Filter out people who are already members of this channel
          const currentMemberIds = members.map(m => m.emp_id);
          const availableEmployees = res.data.filter(emp => !currentMemberIds.includes(emp.emp_id));
          setSearchResults(availableEmployees);
        })
        .catch(err => console.error(err))
        .finally(() => setIsSearching(false));
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, currentEmpId, members]);

  const handleAddMember = async () => {
    if (!selectedEmployee) return;
    try {
      await api.post(`/chat/channels/${channel.id}/members/add`, { emp_id: selectedEmployee.emp_id });
      setSelectedEmployee(null);
      setSearchQuery('');
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to add member.");
    }
  };

  const handleRemoveMember = async (targetEmpId) => {
    if (!window.confirm("Remove this member from the channel?")) return;
    try {
      await api.post(`/chat/channels/${channel.id}/members/remove`, { emp_id: targetEmpId });
      fetchMembers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await api.post(`/chat/channels/${channel.id}/settings`, { image_url: imageUrl });
      onChannelUpdated({ ...channel, image_url: imageUrl });
      onClose();
    } catch(err) { 
      console.error(err); 
    }
  };

  const handleDeleteChannel = async () => {
    if (!window.confirm("Are you sure you want to permanently delete this channel and all its messages?")) return;
    try {
      await api.delete(`/chat/channels/${channel.id}/${currentEmpId}`);
      onChannelDeleted(channel.id);
      onClose();
    } catch (err) {
      alert("Failed to delete channel. You must be the creator to delete it.");
    }
  };

  if (!channel) return null;
  const isCreator = channel.created_by === currentEmpId;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800, color: '#10394f' }}>Channel Settings: {channel.name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
        
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Group Profile Image (URL)</Typography>
            <TextField 
              fullWidth size="small" value={imageUrl} 
              onChange={e => setImageUrl(e.target.value)} 
              placeholder="https://example.com/logo.png" 
              helperText="Paste a direct link to an image."
            />
        </Box>
        
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Members ({members.length})</Typography>
            </Box>
            
            {/* NEW: Autocomplete Add Member Field */}
            {isCreator && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
                <Autocomplete
                  sx={{ flexGrow: 1 }}
                  size="small"
                  options={searchResults}
                  getOptionLabel={(option) => `${option.full_name} (${option.emp_id})`}
                  value={selectedEmployee}
                  onChange={(event, newValue) => setSelectedEmployee(newValue)}
                  inputValue={searchQuery}
                  onInputChange={(event, newInputValue) => setSearchQuery(newInputValue)}
                  loading={isSearching}
                  noOptionsText={searchQuery.length < 2 ? "Type to search..." : "No matching employees found"}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search employee by name or ID..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleAddMember}
                  disabled={!selectedEmployee}
                  startIcon={<PersonAddIcon />}
                  sx={{ whiteSpace: 'nowrap', height: '40px' }}
                >
                  Add
                </Button>
              </Box>
            )}

            <List sx={{ border: '1px solid #eee', borderRadius: 2, maxHeight: 200, overflowY: 'auto' }}>
              {members.map(m => (
                <ListItem key={m.emp_id}>
                  <ListItemText primary={m.name} secondary={m.emp_id} />
                  {m.emp_id !== currentEmpId && isCreator && (
                    <ListItemSecondaryAction>
                      <IconButton edge="end" color="error" onClick={() => handleRemoveMember(m.emp_id)} title="Kick User">
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: 'space-between', borderTop: '1px solid #eee' }}>
        <Box>
            {isCreator && (
                <Button onClick={handleDeleteChannel} color="error" variant="outlined">Delete Channel</Button>
            )}
        </Box>
        <Box>
            <Button onClick={onClose} color="inherit" sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleUpdateSettings} variant="contained">Save Changes</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ChannelSettingsModal;